import {
  PageComponent,
  PageComponentA,
  PageComponentR,
} from "./components/page_component";
import { RadioLayout } from "@tree-company/tree-query";
import { apiClient } from "./client";
import I18n, { translatedPropertyFromKey } from "./locales";
import { FinalPage } from "./views/final_page";

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  return apiClient.treesponses.at(-2)?.getAnswer(key);
}

const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

const namePage = {
  key: "name",
  order: 3,
  component: PageComponent,
  description: translatedPropertyFromKey(["name", "intro", "description"]),
  skipIf: (treesponse) => {
    return (
      !Object.hasOwn(treesponse.answers, "comment") ||
      treesponse.getAnswer("comment") === ""
    );
  },
  sections: [
    {
      order: 1,
      type: "string",
      key: "firstName",
      title: translatedPropertyFromKey(["name", "firstName", "title"]),
      defaultValue: () => getPreviousAnswer("firstName"),
    },
    {
      order: 2,
      type: "string",
      key: "lastName",
      title: translatedPropertyFromKey(["name", "lastName", "title"]),
      defaultValue: () => getPreviousAnswer("lastName"),
    },
    {
      order: 3,
      type: "hidden",
      key: "completed",
      defaultValue: true,
    },
  ],
};

const optinPage = {
  key: "optin",
  order: 4,
  component: PageComponent,
  title: translatedPropertyFromKey(["optin", "intro", "title"]),
  // We only show the opt in page if the user has not encountered it before
  skipIf: () => apiClient.treesponses.some((t) => t.page?.name === "completed"),
  sections: [
    {
      type: "checkbox",
      order: 1,
      title: translatedPropertyFromKey(["optin", "newsletter", "title"]),
      description: translatedPropertyFromKey([
        "optin",
        "newsletter",
        "description",
      ]),
      key: "newsletter",
    },
    {
      type: "email",
      order: 2,
      skipIf: (treesponse) => treesponse.getAnswer("newsletter") !== true,
      dependsOn: ["newsletter"],
      title: translatedPropertyFromKey(["optin", "email", "title"]),
      key: "email",
      customValidations: [
        (treesponse, _key, value) => {
          if (
            treesponse.getAnswer("newsletter") &&
            (value === null || value === undefined)
          ) {
            return [
              false,
              translatedPropertyFromKey(["optin", "email", "required"]),
            ];
          }

          return [true, null];
        },
      ],
    },
  ],
};

export function surveySettings(variant = "a") {
  const finalPage = {
    key: "final",
    order: 5,
    component: FinalPage,
    variant,
    sections: [],
  };
  const abVersion = variant === "a" ? "verA" : "verR";

  return {
    key: "imec-amai",
    pages: [
      {
        key: "statement",
        order: 1,
        component: variant === "a" ? PageComponentA : PageComponentR,
        disablePrevious: true,
        disableNext: true /* to remove button, since a option click brings you to the next page */,
        sections: [
          {
            order: 1,
            type: "radio",
            layout: RadioLayout.buttons,
            key: "opinion",
            title: translatedPropertyFromKey(["survey", "question", variant]),
            defaultValue: (treesponse) => treesponse.getAnswer("opinion"),
            description: translatedPropertyFromKey([
              "statement_question",
              "description",
            ]),
            options: [
              "totally_disagree",
              "disagree",
              "neutral",
              "agree",
              "totally_agree",
            ].map((value) => ({
              label: translatedPropertyFromKey([
                "statement_question",
                "options",
                "compact_version",
                value,
              ]),
              value,
            })),
            required: true,
          },
          {
            order: 2,
            type: "hidden",
            key: "ab_version",
            defaultValue: abVersion,
          },
          {
            order: 99,
            type: "hidden",
            key: "bpart_title",
            dependsOn: ["opinion"],
            computeValue: (treesponse) => {
              const i18n = new I18n("nl");
              const opinion = treesponse.getAnswer("opinion");
              if (opinion === undefined) {
                return "";
              } else {
                return i18n.t([
                  "statement_question",
                  "options",
                  variant,
                  opinion,
                ]);
              }
            },
          },
        ],
      },
      {
        key: "comment",
        order: 2,
        component: PageComponent,
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            title: translatedPropertyFromKey(["survey", "question", variant]),
            maxLength: 500,
            description: {
              nl: (treesponse) => {
                const i18n = new I18n("nl");
                const opinion = treesponse.getAnswer("opinion");
                return i18n.t([
                  "comment",
                  "description",
                  variant,
                  opinion || "fallback",
                ]);
              },
            },
            placeholder: translatedPropertyFromKey(["comment", "placeholder"]),
            aiResponseTexts,
          },
        ],
      },
      namePage,
      optinPage,
      finalPage,
    ],
  };
}
